import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useState } from "react";
import { T, TF } from "react-translator-component";
import { useSelector } from "react-redux";
import {
  Avatar, Box, colors,
  IconButton, ImageList, ImageListItem,
  ListItem, ListItemAvatar, ListItemText,
  Typography,
} from "@mui/material";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import Separator from "../../../components/Separator";
import dayjs from "../../../util/dayjs";
import { convertUserImage, getPostCreatorName } from "../../../util/utils";
import GroupIconImage from "../GroupIconImage";
import PostEventDetailModal from "../postEvent/PostEventDetailModal";
import PostDescSeeMore from "./PostDescSeeMore";
import Likes from "./Likes";
import Thumbnail from "../Thumbnail";
import VotesModal from "./VotesModal";
import PostCommentButton from "./PostCommentButton";
import PrivateUsersModal from "./PrivateUsersModal";
import PostTags from "./PostTags";
import { colors as farmColor } from "../../../common/colors";
import { styles } from "../../../pages/styles";
import profile from "../../../assets/images/profile_img.png";
import HighlightText from "../HighlightText";

interface Props {
  post: any;
  header?: React.ReactNode;
  highlightWords?: string[];
  onClickTag: (tag: string) => void;
  commentStatuses?: string[];
  readonly?: boolean;
}

export default function PostCard({ post, header, highlightWords = [], onClickTag, commentStatuses = [], readonly = false }: Props) {
  // 相対時間の有効化
  dayjs.extend(relativeTime);

  const { admin } = useSelector((state: any) => state.adminAuth);
  const creator: any = post?.creator;
  const [showPrivateUsers, setShowPrivateUsers] = useState<boolean>(false);
  const [showVotesModal, setShowVotesModal] = useState<boolean>(false);
  const [privateUsers, setPrivateUsers] = useState<any[]>([]);
  const [showEventModal, setShowEventModal] = useState<boolean>(false);

  useEffect(() => {
    let userList = post.post_private_users || []
    userList = userList
      .filter((user: any) => user?.privater_type !== admin?.class_name || user?.privater_id !== admin.id)
      .sort((a: any, b:any) => {
        if (a?.privater_type === post.creator_type && a?.privater_id === post.creator_id) {
          return -1
        } else if (b?.privater_type === post.creator_type && b?.privater_id === post.creator_id) {
          return 1
        } else {
          return a?.id - b?.id
        }
      })
    setPrivateUsers(userList || [])
  }, [admin, post])

  const createdAt = (date: any) => {
    return dayjs(date).fromNow()
  }

  const getCreatorName = (post: any) => {
    return getPostCreatorName(post)
  }

  if (!post) {
    return (<></>)
  }
  return (
    <>
      <Box sx={style.container}>
        <ImageList style={style.displayBlock} cols={1}>
          <ImageListItem sx={style.marginBottom20}>
            <ul style={style.paddingLeft13}>
              <ListItem
                className=""
                alignItems="flex-start"
                style={style.listItem}
              >
                <ListItemAvatar style={style.listItemAvatar}>
                  {post.member_group ? (
                    <GroupIconImage
                      groupId={post.member_group?.id}
                      alt={post.member_group?.name}
                      style={style.groupIconImage}
                    />
                  ) : (
                    post.is_anonymous === 3 ? (
                      <Avatar style={style.margin0} alt="Remy Sharp" src={profile} />
                    ) : (
                      <Avatar
                        alt="img"
                        style={style.margin0}
                        src={creator?.image ? convertUserImage(creator?.image) : profile}
                      />
                    )
                  )}
                </ListItemAvatar>

                <ListItemText
                  primary={
                    post.member_group ? (
                      <>{post.member_group?.name}</>
                    ) : (
                      <Typography sx={style.fontSize16} component="span" variant="body2" color="text.primary">
                        {getCreatorName(post)}
                      </Typography>
                    )
                  }
                />

                {header}
              </ListItem>
            </ul>

            <div>
              <Thumbnail item={post} type="posts" />
            </div>

            <div style={style.marginHorizontal20}>
              <div style={style.justifyContentCenter}>
                <Likes
                  iconSize={"lg"}
                  liketype={"post"}
                  item={post}
                />
              </div>

              <HighlightText
                text={post.title}
                highlightWords={highlightWords}
                sx={style.title}
              />

              {post.is_private && (
                <Box onClick={() => setShowPrivateUsers(true)} sx={style.privatePostBox}>
                  <Typography sx={style.privatePostUserText}>{TF(T("mng_can_view_post_user_count"), privateUsers.length)}</Typography>
                  <Typography sx={style.seePrivatePostUser}>{T("mng_view_users")}</Typography>
                </Box>
              )}

              {/* 本文 */}
              <PostDescSeeMore description={post.details} highlightWords={highlightWords} />
            </div>

            {/* タグ */}
            <div style={style.tag}>
              <PostTags
                tags={post.post_tags || []}
                highlightWords={highlightWords}
                onClick={onClickTag}
              />
            </div>
            <Separator color={farmColor.farm_gray100} sx={style.tagSeparator} />

            {post.is_event && (
              <span
                style={style.eventMore}
                onClick={() => setShowEventModal(true)}
              >
                {T("mng_event_detail")}
              </span>
            )}

            <div style={style.marginHorizontal20}>
              <div>
                {post.enable_comments && (
                  <PostCommentButton
                    post={post}
                    highlightWords={highlightWords}
                    commentStatuses={commentStatuses}
                  />
                )}

                {post.enable_participants && (
                  <Box>
                    <IconButton
                      onClick={() => setShowVotesModal(true)}
                      aria-label="thumbs"
                    >
                      <HowToVoteIcon />
                      <Typography>{T("mng_post_vote_status")}</Typography>
                    </IconButton>
                  </Box>
                )}
              </div>
              <Typography color={farmColor?.farm_gray200}>{createdAt(post.created_at)}</Typography>
            </div>
          </ImageListItem>
        </ImageList>
      </Box>

      <VotesModal
        open={showVotesModal}
        post={post}
        highlightWords={highlightWords}
        closeModal={() => setShowVotesModal(false)}
      />

      <PrivateUsersModal
        open={showPrivateUsers}
        post={post}
        privateUsers={privateUsers}
        highlightWords={highlightWords}
        onChange={(users: any[]) => setPrivateUsers(users)}
        closeModal={() => setShowPrivateUsers(false)}
        readonly={readonly}
      />

      {post.is_event &&
        <PostEventDetailModal
          open={showEventModal}
          post={post}
          highlightWords={highlightWords}
          onClose={() => setShowEventModal(false)}
        />
      }
    </>
  );
}

const style = {
  displayBlock: {
    display: "block",
    overflow: "visible"
  },
  marginBottom20: {
    marginBottom: "20px",
  },
  paddingLeft13: {
    paddingLeft: 13,
  },
  listItem: {
    ...styles.detailListItem,
    paddingLeft: 0,
    paddingRight: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  listItemAvatar: { margin: "0 7px 0 0", minWidth: 40 },
  groupIconImage: {
    margin: 0,
    width: 40,
    height: 40,
    borderRadius: "50%"
  },
  margin0: { margin: 0 },
  fontSize16: { fontSize: 16 },
  marginHorizontal20: { margin: "0 20px" },
  justifyContentCenter: { justifyContent: "center" },
  title: {
    marginLeft: "6px",
    marginRight: "6px",
    fontSize: 20,
    fontWeight: "600",
    overflowWrap: "break-word" as const
  },
  privatePostBox: {
    display: "flex",
    margin: "0px 5px"
  },
  privatePostUserText: {
    fontSize: 12,
    marginRight: "10px"
  },
  seePrivatePostUser: {
    fontSize: 12,
    color: farmColor.farm_gray200
  },
  date: {
    color: "#A2A2A2",
    fontSize: 12,
    marginLeft: 1
  },
  tag: { margin: "10px 0 5px", padding: "0 20px" },
  tagSeparator: {
    width: "100%",
    height: "1.5px",
    margin: `0 calc(50% -40px)`,
    padding: "0 -20px"
  },
  container: {
    paddingTop: "20px",
    paddingBottom: "50px",
    border: `1px solid ${colors.grey[400]}`,
    overflow: "visible" as const,
    width: "calc(33.04% - 10px)",
    minWidth: "320px",
    '@media (max-width: 1255px)': {
      width: "calc(50% - 10px)",
    },
  },
  eventMore: {
    fontSize: 12,
    color: farmColor.farm_gray200,
    cursor: "pointer",
    display: "block",
    marginTop: 8,
    marginLeft: 26,
  },
};
